import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card, CardSection, Flex, IconSvg, Text } from '@/lib/v2/components';
import { SectionHeader } from '@/lib/v2/examples/SectionHeader';
import { EditPencilIcon } from '@/lib/v2/icons/outline';
import { CampaignsIcon } from '@/lib/v2/icons/solid';

import { EventListenerType } from '@/src/application/hooks/enums/EventListenerEnum';
import IframeTransport from '@/src/compat/iframeTransport';
import { SendActionsPostMessage } from '@/src/compat/sendActionsPostMessage';
import useMessage from '@/src/compat/useMessage';
import { useCampaignsService } from '@/src/infrastructure/Protocol/Campaigns/useCampaignsService';
import { atomInfoCampaignV1 } from '@/src/modules/CampaignsModule/atoms/campaignAtom';
import { ICampaignDetailResponse } from '@/src/modules/CampaignsModule/interfaces/Campaigns';

const CampaignReport = () => {
  const { id } = useParams();
  const [showSpinnerByAction, setShowSpinnerByAction] = useState<boolean>(true);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [infoCampaignV1, setInfoCampaignV1] = useAtom(atomInfoCampaignV1);
  const haveData = useMemo(() => infoCampaignV1?.campaign?.name, [infoCampaignV1]);
  const navigate = useNavigate();
  const service = useCampaignsService();
  const { t } = useTranslation();

  useMessage(EventListenerType.Loaded, (_) => {
    showSpinnerByAction && setIsLoaded(true);
  });

  useEffect(() => {
    const type = 'manual';
    isLoaded &&
      showSpinnerByAction &&
      SendActionsPostMessage('Campaign', 'ViewReport', { id, type });
  }, [showSpinnerByAction, isLoaded, id]);

  const getCampaignData = async () => {
    if (!id) return;

    const response: ICampaignDetailResponse = await service.getCampaignByID(parseInt(id));

    setInfoCampaignV1({
      campaign: {
        id: parseInt(id || '0'),
        name: response?.campaign?.name ?? '',
      },
    });
  };

  useMessage(EventListenerType.LoadedReport, (_) => {
    if (isLoaded && showSpinnerByAction) {
      if (!haveData) {
        void getCampaignData();
      }
      setShowSpinnerByAction(false);
    }
  });

  useEffect(() => {
    SendActionsPostMessage('Campaign', 'VerifyLoaded');
  }, []);

  return (
    <>
      <Card>
        <CardSection noPadding>
          <Flex column>
            <SectionHeader
              backButton
              sticky
              icon={<CampaignsIcon />}
              isLoading={showSpinnerByAction}
              titleNode={
                <Text
                  as="h3"
                  className="!2xl:text-20 !text-16"
                  fontWeight="medium"
                  variant="title-1"
                >
                  <Flex withGap>
                    {t('REPORT')}
                    {' / '}
                    <Text color="primary">{infoCampaignV1?.campaign?.name}</Text>
                    <Button
                      link
                      className="!h-auto !p-0"
                      iconLeft={
                        <IconSvg height="16px" svgComponent={<EditPencilIcon />} width="16px" />
                      }
                      id="back-to-campaigns-button"
                      onClick={() => alert('Edit')}
                    />
                  </Flex>
                </Text>
              }
              onBack={() => navigate && navigate(-1)}
            />
            <Flex column withGap gapSize="medium">
              <IframeTransport loading={showSpinnerByAction} />
            </Flex>
          </Flex>
        </CardSection>
      </Card>
    </>
  );
};

export default CampaignReport;
